/**
 * Pure, dependency-free helpers for consuming the resumable background chat
 * stream (`/api/chat/bg-stream/{chatId}?cursor=N`). Kept out of Playground.tsx
 * so the fiddly SSE-frame parsing + resilience policy is unit-testable in
 * isolation (see streamConsumer.test.ts).
 *
 * The backend tees EVERY generation frame (text deltas, tool actions,
 * deep_research_*, quota, end) into a durable per-chat event log and stamps
 * each non-terminal frame with `id: <cursor>`. A client tracks the id of the
 * last frame it applied and reconnects with `?cursor=<id>` to resume exactly
 * where it left off — so the parser must read `event:`/`data:`/`id:` lines,
 * not just `data:`.
 */

import { ContentItem } from '../shared/interfaces';

/** A Panorama snapshot replaces prose; tool/reasoning metadata survives. */
export function replaceStreamTextItems(items: ContentItem[], text: string): ContentItem[] {
  return [...items.filter(item => item.type !== 'text'), { type: 'text', text }];
}

export interface SSEFrame {
  event: string;
  data: string;
  id: string | null;
}

/**
 * Split a (possibly partial) text buffer into complete SSE frames. Frames are
 * separated by a blank line. Returns the parsed frames plus any trailing
 * incomplete text that must be carried over to the next read.
 */
export function parseSSEFrames(buffer: string): { frames: SSEFrame[]; rest: string } {
  // The server ends SSE lines with "\r\n" and a read can end exactly between
  // the "\r" and the "\n". Normalizing that bare trailing "\r" to "\n" now
  // would fabricate a phantom blank line once the "\n" arrives on the next
  // read — the half-received frame would be emitted with data "" and its data
  // line would leak into a separate default "message" frame
  // (CHAT-MARITACA-2B/2P/2F). Withhold the "\r" and carry it in `rest`.
  let splittable = buffer;
  let heldCR = "";
  if (splittable.endsWith("\r")) {
    heldCR = "\r";
    splittable = splittable.slice(0, -1);
  }
  const normalized = splittable.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const blocks = normalized.split("\n\n");
  const rest = (blocks.pop() ?? "") + heldCR; // last block may be incomplete
  const frames: SSEFrame[] = [];
  for (const block of blocks) {
    const frame = parseFrameBlock(block);
    if (frame) frames.push(frame);
  }
  return { frames, rest };
}

function parseFrameBlock(block: string): SSEFrame | null {
  let event = "message";
  let id: string | null = null;
  const dataLines: string[] = [];
  let sawField = false;

  for (const line of block.split("\n")) {
    if (!line || line.startsWith(":")) continue; // blank line or comment
    const idx = line.indexOf(":");
    const field = idx === -1 ? line : line.slice(0, idx);
    let value = idx === -1 ? "" : line.slice(idx + 1);
    if (value.startsWith(" ")) value = value.slice(1);

    if (field === "data") {
      dataLines.push(value);
      sawField = true;
    } else if (field === "event") {
      event = value || "message";
      sawField = true;
    } else if (field === "id") {
      id = value;
      sawField = true;
    }
    // retry / unknown fields are ignored
  }

  if (!sawField) return null;
  return { event, data: dataLines.join("\n"), id };
}

/**
 * Safely parse a stream frame's `data` payload as JSON.
 *
 * When a generation stream drops mid-frame, the SSE loader flushes the
 * trailing *incomplete* event block (see sse.js `_onStreamLoaded`), so a
 * consumer can be handed a truncated payload such as `{"t`. Calling
 * `JSON.parse` on that throws `SyntaxError: Unterminated string in JSON` and,
 * when unguarded, crashes the entire chat view (Sentry CHAT-MARITACA-1Z).
 *
 * Returns the parsed value, or `null` when the payload is empty or not valid
 * JSON. Callers must skip a `null` result rather than letting the parse throw.
 */
export function parseStreamData(data: string): any | null {
  if (!data) return null;
  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
}

// --- Resilience policy ------------------------------------------------------

/** Stall watchdog: declare the stream dead after this much silence. The server
 *  pings every 0.5s, so 25s == 50 missed pings — unambiguously stalled. */
export const INACTIVITY_TIMEOUT_MS = 25000;

/** Backoff schedule for auto-reconnect attempts (ms). Length == max attempts. */
export const RECONNECT_BACKOFF_MS = [2000, 5000, 10000];
export const MAX_RECONNECT_ATTEMPTS = RECONNECT_BACKOFF_MS.length;

export function getReconnectDelayMs(attempt: number): number {
  const i = Math.max(0, Math.min(attempt, RECONNECT_BACKOFF_MS.length - 1));
  return RECONNECT_BACKOFF_MS[i];
}

/**
 * Whether an SSE error is transient (worth auto-reconnecting from) vs. a fatal
 * application error that should surface to the user. status 0/undefined is a
 * network drop or our synthetic stall; 5xx gateway codes are transient proxy
 * blips; auth/quota/validation (4xx) are fatal.
 */
export function isTransientError(status: number | undefined | null): boolean {
  if (status === undefined || status === null || status === 0) return true;
  return status === 502 || status === 503 || status === 504;
}

export function buildBgStreamUrl(apiBase: string, chatId: string, cursor: number, windowSeconds?: number): string {
  const c = Number.isFinite(cursor) && cursor > 0 ? Math.floor(cursor) : 0;
  const w = windowSeconds && windowSeconds > 0 ? `&window=${windowSeconds}` : "";
  return `${apiBase}/api/chat/bg-stream/${encodeURIComponent(chatId)}?cursor=${c}${w}`;
}

// --- Redes que seguram a resposta inteira (proxy corporativo) ----------------
//
// Gateways de segurança (Skyhigh, Cisco Umbrella, alguns tenants Netskope,
// PROCERGS, proxies de universidade) só repassam a resposta quando ela termina.
// O SSE ao vivo nunca chega aos poucos: o usuário fica em "Pensando..." e, aos
// 25s, o watchdog de inatividade dispara. Em prod (out/2026) foram 29 cortes em
// ~25s com retomada imediata, de 18 IPs, todos desses gateways.
//
// Detecção: o servidor manda ping a cada 0,5s, então nenhum byte de resposta
// em FIRST_BYTE_TIMEOUT_MS (com o run já gerando) só acontece quando algo no
// caminho está segurando a resposta. Aí o chat passa a ler o bg-stream em
// janelas de BG_STREAM_WINDOW_SECONDS: respostas curtas e completas, que o
// proxy libera logo, cada uma continuando do cursor da anterior.

/** Sem nenhum byte de resposta por este tempo → suspeita de proxy segurando. */
export const FIRST_BYTE_TIMEOUT_MS = 5000;
/** Rede já marcada como bufferizada nesta aba: troca mais cedo. */
export const KNOWN_BUFFERED_FIRST_BYTE_TIMEOUT_MS = 1500;
/** Duração de cada janela do bg-stream (o servidor limita a [0,5; 10]). */
export const BG_STREAM_WINDOW_SECONDS = 2;
/**
 * Prazo de uma janela inteira (request + leitura). O servidor fecha em ~2s;
 * passou disso, a conexão está pendurada: aborta e reabre do último cursor
 * aplicado (sem perder nem duplicar texto).
 */
export const BG_STREAM_WINDOW_TIMEOUT_MS = 15000;

class AbortedError extends Error {}

/** Rejeita assim que `signal` abortar, mesmo se `p` nunca resolver. */
function untilAborted<T>(p: Promise<T>, signal: AbortSignal): Promise<T> {
  if (signal.aborted) return Promise.reject(new AbortedError("aborted"));
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(new AbortedError("aborted"));
    signal.addEventListener("abort", onAbort, { once: true });
    p.then(
      v => { signal.removeEventListener("abort", onAbort); resolve(v); },
      e => { signal.removeEventListener("abort", onAbort); reject(e); },
    );
  });
}

/** AbortController que também aborta quando `parent` abortar. */
function childController(parent?: AbortSignal): { ctrl: AbortController; release: () => void } {
  const ctrl = new AbortController();
  if (!parent) return { ctrl, release: () => {} };
  const onAbort = () => ctrl.abort();
  if (parent.aborted) ctrl.abort();
  else parent.addEventListener("abort", onAbort, { once: true });
  return { ctrl, release: () => parent.removeEventListener("abort", onAbort) };
}

export type BgStreamOnceOutcome = "end" | "buffered" | "cancelled";

/**
 * Lê o bg-stream clássico (uma resposta até o `end`). Com `firstByteTimeoutMs`,
 * nenhum byte nesse prazo (o servidor pinga a cada 0,5s) = proxy segurando a
 * resposta: aborta o request e devolve "buffered" para o chamador trocar para
 * as janelas. Fim sem `end` ou HTTP de erro lançam (o chamador reconecta).
 * `onFrame` devolve true no frame terminal.
 */
export async function readBgStreamOnce(opts: {
  fetchStream: (signal: AbortSignal) => Promise<Response>;
  onFrame: (frame: SSEFrame) => boolean | Promise<boolean>;
  isCancelled: () => boolean;
  firstByteTimeoutMs: number | null;
  signal?: AbortSignal;
}): Promise<BgStreamOnceOutcome> {
  const { ctrl, release } = childController(opts.signal);
  let gotByte = false;
  let buffered = false;
  const timer = opts.firstByteTimeoutMs != null
    ? setTimeout(() => { if (!gotByte) { buffered = true; ctrl.abort(); } }, opts.firstByteTimeoutMs)
    : null;
  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  try {
    const response = await untilAborted(opts.fetchStream(ctrl.signal), ctrl.signal);
    if (!response.ok || !response.body) throw new Error(`bg-stream HTTP ${response.status}`);
    reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    for (;;) {
      const { done, value } = await untilAborted(reader.read(), ctrl.signal);
      if (done) break;
      if (!gotByte) {
        gotByte = true;
        if (timer) clearTimeout(timer);
      }
      buffer += decoder.decode(value, { stream: true });
      const parsed = parseSSEFrames(buffer);
      buffer = parsed.rest;
      for (const frame of parsed.frames) {
        if (opts.isCancelled()) return "cancelled";
        if (await opts.onFrame(frame)) return "end";
      }
    }
    if (opts.isCancelled()) return "cancelled";
    throw new Error("bg-stream ended before terminal end event");
  } catch (e) {
    if (buffered) return "buffered";
    if (opts.isCancelled() || opts.signal?.aborted) return "cancelled";
    throw e;
  } finally {
    if (timer) clearTimeout(timer);
    release();
    try { reader?.cancel().catch(() => {}); } catch {}
    ctrl.abort();
  }
}

/** Cursor de um frame `window` (`{"cursor": n}`), ou null. */
export function windowCursorFromFrame(frame: SSEFrame): number | null {
  if (frame.event !== "window") return null;
  try {
    const n = JSON.parse(frame.data)?.cursor;
    return typeof n === "number" && Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
  } catch {
    return null;
  }
}

export type WindowedPollOutcome = "end" | "cancelled" | "failed";

/**
 * Lê o bg-stream em janelas curtas até o `end`. Cada janela é um request
 * novo a partir do cursor do último frame aplicado (ou do cursor do frame
 * `window`), então uma janela que cai no meio continua sem duplicar texto.
 * Uma janela que passa de `windowTimeoutMs` (conexão pendurada) é abortada e
 * conta como queda; abortar `signal` libera na hora um request/leitura pendente.
 * `onFrame` recebe todos os frames menos o `window` (controle), incluindo o `end`.
 */
export async function pollBgStreamWindows(opts: {
  fetchWindow: (cursor: number, signal: AbortSignal) => Promise<Response>;
  onFrame: (frame: SSEFrame) => void | Promise<void>;
  isCancelled: () => boolean;
  startCursor?: number;
  maxAttempts?: number;
  sleep?: (ms: number) => Promise<void>;
  signal?: AbortSignal;
  windowTimeoutMs?: number;
}): Promise<WindowedPollOutcome> {
  const sleep = opts.sleep ?? ((ms: number) => new Promise<void>(r => setTimeout(r, ms)));
  const maxAttempts = opts.maxAttempts ?? MAX_RECONNECT_ATTEMPTS;
  const windowTimeoutMs = opts.windowTimeoutMs ?? BG_STREAM_WINDOW_TIMEOUT_MS;
  const cancelled = () => opts.isCancelled() || Boolean(opts.signal?.aborted);
  let cursor = opts.startCursor && opts.startCursor > 0 ? opts.startCursor : 0;
  let attempt = 0;
  while (!cancelled()) {
    const { ctrl, release } = childController(opts.signal);
    const timer = setTimeout(() => ctrl.abort(), windowTimeoutMs);
    let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
    try {
      const response = await untilAborted(opts.fetchWindow(cursor, ctrl.signal), ctrl.signal);
      if (!response.ok || !response.body) throw new Error(`bg-stream HTTP ${response.status}`);
      reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let sawWindow = false;
      for (;;) {
        const { done, value } = await untilAborted(reader.read(), ctrl.signal);
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parsed = parseSSEFrames(buffer);
        buffer = parsed.rest;
        for (const frame of parsed.frames) {
          if (cancelled()) return "cancelled";
          const next = windowCursorFromFrame(frame);
          if (next !== null) {
            cursor = Math.max(cursor, next);
            sawWindow = true;
            continue;
          }
          cursor = cursorFromFrame(frame, cursor);
          await opts.onFrame(frame);
          if (isTerminalEvent(frame.event)) return "end";
        }
      }
      if (!sawWindow) throw new Error("bg-stream window closed without window/end frame");
      attempt = 0;
    } catch {
      if (cancelled()) return "cancelled";
      if (attempt >= maxAttempts) return "failed";
      clearTimeout(timer);
      await sleep(getReconnectDelayMs(attempt));
      attempt += 1;
    } finally {
      clearTimeout(timer);
      release();
      try { reader?.cancel().catch(() => {}); } catch {}
      ctrl.abort();
    }
  }
  return "cancelled";
}

// --- Frame classification ---------------------------------------------------

export const DEEP_RESEARCH_EVENTS = [
  "deep_research_status",
  "deep_research_plan",
  "deep_research_step",
  "deep_research_metadata",
];

export function isDeepResearchEvent(event: string): boolean {
  return DEEP_RESEARCH_EVENTS.indexOf(event) !== -1;
}

export function isTerminalEvent(event: string): boolean {
  return event === "end";
}

/** Parse the `id` of a frame into a resume cursor, falling back to `prev`. */
export function cursorFromFrame(frame: SSEFrame, prev: number): number {
  if (frame.id == null || frame.id === "") return prev;
  const n = parseInt(frame.id, 10);
  return Number.isFinite(n) && n > prev ? n : prev;
}

// --- Text application policy ------------------------------------------------

/**
 * Apply one `message` frame's text to the text rendered so far.
 *
 * Two incompatible contracts share the same `message` event:
 *   - normal generations stream DELTAS (the client sends `delta: true`) → append;
 *   - deep research streams the report ACCUMULATED, i.e. every frame carries the
 *     whole report written so far (see `deep_research_agent.py`: "contrato
 *     legado: `message` carrega o texto ACUMULADO, nao delta") → replace.
 *
 * Appending accumulated snapshots renders the report as a pyramid of prefixes
 * (snapshot_1 + snapshot_2 + ... each restarting from the title).
 */
export function applyStreamText(prevText: string, incoming: string, accumulated: boolean): string {
  return accumulated ? incoming : prevText + incoming;
}

export interface AccumulatedTextRuns {
  /** Arm/disarm a chat at send time from the request body's `deep_research`. */
  start(chatId: string, accumulated: boolean): void;
  /** Arm from the stream itself: any `deep_research_*` frame means accumulated. */
  noteEvent(chatId: string, event: string): void;
  isAccumulated(chatId: string): boolean;
  clear(chatId: string): void;
}

/**
 * Tracks which chats have an in-flight run whose `message` frames carry
 * ACCUMULATED text.
 *
 * The signal must come from the RUN, never from the composer toggle: deep
 * research also starts from the `propor_deep_research` card — which runs it with
 * the toggle off (`opts.deepResearch`) — so a toggle-based decision appended the
 * accumulated snapshots and rendered the duplicated report users reported.
 * `noteEvent` additionally arms the chat straight from the stream, which covers
 * every path (card, toggle, resume, a run turned on server-side).
 */
export function createAccumulatedTextRuns(): AccumulatedTextRuns {
  const chats = new Set<string>();
  return {
    start(chatId: string, accumulated: boolean) {
      if (!chatId) return;
      if (accumulated) chats.add(chatId);
      else chats.delete(chatId); // a fresh normal run goes back to delta append
    },
    noteEvent(chatId: string, event: string) {
      if (chatId && isDeepResearchEvent(event)) chats.add(chatId);
    },
    isAccumulated(chatId: string) {
      return !!chatId && chats.has(chatId);
    },
    clear(chatId: string) {
      chats.delete(chatId);
    },
  };
}
